import { Button, ExpandableSearch, Skeleton } from "@somethings-ui/web";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Bell, Pencil, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mergeNotesAndCallLogs } from "@/components/conversation-details/notes/merge-notes-and-call-logs";
import { useGetCallLogs } from "@/lib/api/calls";
import { useCurrentUser } from "@/lib/api/user";
import {
  useCreateNote,
  useDeleteNote,
  useEditNote,
  useNotes,
  type Note,
} from "@/lib/api/notes";
import { useAbility } from "@/lib/casl/ability-context";
import { cn } from "@/lib/utils";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { useActionPanelScrollRef } from "../action-panel-scroll";
import type { ActionSurfaceCapabilities } from "../action-capabilities";
import type { UseActionAnalyticsResult } from "../use-action-analytics";
import { ContextCallLogCard } from "./context-call-log-card";
import { ContextNoteCard } from "./context-note-card";
import { ContextNoteEditor, type ContextNoteSubmit } from "./context-note-editor";
import {
  canEditNote,
  filterNotes,
  persistNoteTitle,
  resolveDisplayTitle,
  resolveNoteSource,
  sortNotesByCreatedDesc,
} from "./context-note-utils";

const FEED_ROW_ESTIMATE_PX = 120;
const FEED_ROW_OVERSCAN = 6;

interface ContextNotesProps {
  mentorshipId: string | null;
  capabilities: ActionSurfaceCapabilities;
  analytics: UseActionAnalyticsResult;
  focusNoteId?: string | null;
  onFocusNoteHandled?: () => void;
}

export function ContextNotes({
  mentorshipId,
  capabilities,
  analytics,
  focusNoteId = null,
  onFocusNoteHandled,
}: ContextNotesProps) {
  const ability = useAbility();
  const canCreate =
    capabilities.canEditNotes && ability.can("create", "documentation");
  const canUpdate =
    capabilities.canEditNotes && ability.can("update", "documentation");

  const scrollRef = useActionPanelScrollRef();
  const { data: user } = useCurrentUser();
  const { data: notes = [], isLoading: notesLoading } = useNotes(
    mentorshipId ?? "",
  );
  const { data: callLogs = [], isLoading: callLogsLoading } = useGetCallLogs(
    mentorshipId ?? undefined,
  );
  const { mutate: createNote, isPending: isCreating } = useCreateNote();
  const { mutate: editNote } = useEditNote();
  const { mutate: deleteNote, isPending: isDeleting } = useDeleteNote();

  const [search, setSearch] = useState("");
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [focusedNoteId, setFocusedNoteId] = useState<string | null>(null);
  const wasSearching = useRef(false);

  const visibleNotes = useMemo(
    () => sortNotesByCreatedDesc(filterNotes(notes, search)),
    [notes, search],
  );

  const feed = useMemo(
    () => mergeNotesAndCallLogs(visibleNotes, callLogs),
    [visibleNotes, callLogs],
  );

  const virtualizer = useVirtualizer({
    count: feed.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => FEED_ROW_ESTIMATE_PX,
    overscan: FEED_ROW_OVERSCAN,
    getItemKey: (index) => feed[index]?.id ?? index,
  });

  useEffect(() => {
    if (!focusNoteId || notesLoading) return;

    const noteExists = notes.some((note) => note.id === focusNoteId);
    if (!noteExists) {
      onFocusNoteHandled?.();
      return;
    }

    const noteVisible = visibleNotes.some((note) => note.id === focusNoteId);
    if (!noteVisible) {
      if (search.trim().length > 0) {
        setSearch("");
        setSearchExpanded(false);
      }
      return;
    }

    const index = feed.findIndex(
      (item) => item.type === "note" && item.id === focusNoteId,
    );
    if (index < 0) {
      onFocusNoteHandled?.();
      return;
    }

    setFocusedNoteId(focusNoteId);
    virtualizer.scrollToIndex(index, { align: "center", behavior: "smooth" });
    onFocusNoteHandled?.();
  }, [
    feed,
    focusNoteId,
    notes,
    notesLoading,
    onFocusNoteHandled,
    search,
    virtualizer,
    visibleNotes,
  ]);

  useEffect(() => {
    if (!focusedNoteId) return;
    const timeout = window.setTimeout(() => setFocusedNoteId(null), 2500);
    return () => window.clearTimeout(timeout);
  }, [focusedNoteId]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      const active = value.trim().length > 0;
      if (active && !wasSearching.current) {
        analytics.trackNotesSearch(filterNotes(notes, value).length);
      }
      wasSearching.current = active;
    },
    [analytics, notes],
  );

  const handleCreate = useCallback(
    ({ content, reminder, reminderTimezone, title }: ContextNoteSubmit) => {
      if (!mentorshipId) return;
      createNote({
        title: persistNoteTitle(title),
        content,
        mentorshipId,
        reminder,
        metadata: reminderTimezone ? { reminderTimezone } : undefined,
      });
      analytics.trackNoteAdd("mentor");
      setIsAdding(false);
    },
    [analytics, createNote, mentorshipId],
  );

  const handleEdit = useCallback(
    (
      note: Note,
      { content, reminder, reminderTimezone, title }: ContextNoteSubmit,
    ) => {
      if (!mentorshipId) return;
      const metadata = { ...note.metadata };
      if (reminderTimezone) metadata.reminderTimezone = reminderTimezone;
      else delete metadata.reminderTimezone;
      editNote({
        id: note.id,
        mentorshipId,
        title: persistNoteTitle(title),
        content,
        reminder,
        metadata,
      });
      analytics.trackNoteEdit(resolveNoteSource(note));
      setEditingId(null);
    },
    [analytics, editNote, mentorshipId],
  );

  const handleDelete = useCallback(
    (note: Note) => {
      if (!mentorshipId) return;
      deleteNote({ id: note.id, mentorshipId });
      analytics.trackNoteDelete(resolveNoteSource(note));
      setEditingId(null);
    },
    [analytics, deleteNote, mentorshipId],
  );

  const isLoading = notesLoading || callLogsLoading;
  const hasNotes = notes.length > 0;
  const hasFeedItems = feed.length > 0;

  if (isLoading) {
    return (
      <section className="px-4 pt-4 pb-3">
        <NotesHeaderLabel />
        <div className="mt-3 flex flex-col gap-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </section>
    );
  }

  if (!hasNotes && callLogs.length === 0 && !isAdding) {
    return (
      <section className="px-4 pt-4 pb-3">
        <NotesEmptyCard
          canCreate={canCreate}
          onWrite={() => setIsAdding(true)}
        />
      </section>
    );
  }

  return (
    <section className="px-4 pt-4 pb-3">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "overflow-hidden whitespace-nowrap transition-all duration-200 ease-out",
            searchExpanded ? "max-w-0 opacity-0" : "max-w-[240px] opacity-100",
          )}
        >
          <NotesHeaderLabel />
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          {hasNotes ? (
            <ExpandableSearch
              value={search}
              onChange={handleSearchChange}
              expanded={searchExpanded}
              onExpandedChange={setSearchExpanded}
              placeholder="Search documentation"
              iconClassName="text-text-tertiary"
            />
          ) : null}
          {canCreate && !searchExpanded ? (
            <NotesAddButton onClick={() => setIsAdding(true)} />
          ) : null}
        </div>
      </div>

      {isAdding ? (
        <div className="mt-3">
          <ContextNoteEditor
            pending={isCreating}
            onSubmit={handleCreate}
            onCancel={() => setIsAdding(false)}
          />
        </div>
      ) : null}

      {!hasFeedItems ? (
        search.trim().length > 0 ? (
          <p className="mt-3 font-body text-body-3 text-text-tertiary">
            No documentation matches that search.
          </p>
        ) : null
      ) : (
        <div
          className="relative mt-1 w-full"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const item = feed[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 top-0 w-full"
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                {item.type === "call" ? (
                  <ContextCallLogCard call={item} />
                ) : editingId === item.id &&
                  canEditNote({
                    note: item,
                    currentUserId: user?.id,
                    canUpdateDocumentation: canUpdate,
                  }) ? (
                  <div className="py-3">
                    <ContextNoteEditor
                      initialTitle={resolveDisplayTitle(item) ?? ""}
                      initialContent={item.content}
                      initialReminder={item.reminder}
                      initialReminderTimezone={
                        (item.metadata?.reminderTimezone as string) ?? null
                      }
                      onSubmit={(values) => handleEdit(item, values)}
                      onCancel={() => setEditingId(null)}
                    />
                  </div>
                ) : (
                  <div
                    className={
                      focusedNoteId === item.id
                        ? "rounded-xl ring-2 ring-states-active ring-offset-2 ring-offset-surfaces-z1"
                        : undefined
                    }
                  >
                    <ContextNoteCard
                      note={item}
                      canEdit={canEditNote({
                        note: item,
                        currentUserId: user?.id,
                        canUpdateDocumentation: canUpdate,
                      })}
                      onEdit={() => setEditingId(item.id)}
                      onDelete={() => setNoteToDelete(item)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmActionDialog
        isOpen={noteToDelete !== null}
        onClose={() => setNoteToDelete(null)}
        onConfirm={() => {
          if (noteToDelete) handleDelete(noteToDelete);
          setNoteToDelete(null);
        }}
        loading={isDeleting}
        title="Delete note"
        description="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
      />
    </section>
  );
}

function NotesHeaderLabel() {
  return (
    <h3 className="font-mono text-xs font-medium tracking-[-0.24px] text-text-tertiary">
      DOCUMENTATION
    </h3>
  );
}

function NotesAddButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 rounded-xl p-1.5 transition-colors hover:bg-surfaces-z0"
    >
      <Plus className="size-4 text-text-tertiary" />
      <span className="font-body text-body-3 text-text-tertiary">Add</span>
    </button>
  );
}

function NotesEmptyCard({
  canCreate,
  onWrite,
}: {
  canCreate: boolean;
  onWrite: () => void;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-[20px] bg-surfaces-z3 p-4">
      <div className="flex items-center justify-between gap-2">
        <NotesHeaderLabel />
        {canCreate ? <NotesAddButton onClick={onWrite} /> : null}
      </div>

      <div className="flex flex-col gap-1">
        <p className="font-body text-body-1 text-text-primary">
          Your private space to track what matters
        </p>
        <p className="font-body text-body-2 text-text-secondary">
          Jot down things you notice, reminders for next time, or anything you
          want to remember. AI will also surface patterns it picks up from your
          conversations.
        </p>
      </div>

      <div className="flex flex-col gap-2 py-2">
        <div className="flex items-center gap-2">
          <Pencil className="size-3 shrink-0 text-text-tertiary" />
          <span className="font-body text-body-3 text-text-tertiary">
            Write documentation after check-ins to build context over time
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Bell className="size-3 shrink-0 text-text-tertiary" />
          <span className="font-body text-body-3 text-text-tertiary">
            Set reminders so nothing slips through the cracks
          </span>
        </div>
      </div>

      {canCreate ? (
        <div>
          <Button variant="primary" size="sm" onClick={onWrite}>
            Write a note
          </Button>
        </div>
      ) : null}
    </div>
  );
}
